import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  useVoiceTranscription,
  type UseVoiceTranscriptionOptions,
} from "@/hooks/useVoiceTranscription";
import { cn } from "@/lib/utils";
import { MicIcon, SquareIcon, WifiOffIcon } from "lucide-react";
import type { ComponentProps } from "react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

const BAR_COUNT = 10;
const FFT_SIZE = 256;
const BAR_WIDTH = 3;
const BAR_GAP = 2;
const MAX_HEIGHT = 20;
const MIN_HEIGHT = 3;

/**
 * Logarithmic frequency band boundaries for speech-focused visualization.
 * Human speech sits mostly between 85 Hz and 4 kHz. With FFT_SIZE=256 at 48 kHz
 * sample rate we get 128 bins (~187 Hz each). These ranges spread voice energy
 * across all bars instead of concentrating it in the first bin.
 */
const BAND_RANGES: [number, number][] = [
  [1, 2],   // ~187–375 Hz  — low fundamental
  [2, 3],   // ~375–560 Hz  — high fundamental
  [3, 5],   // ~560–935 Hz  — first harmonics low
  [5, 7],   // ~935–1.3 kHz — first harmonics high
  [7, 10],  // ~1.3–1.9 kHz — vowel formants low
  [10, 14], // ~1.9–2.6 kHz — vowel formants high
  [14, 19], // ~2.6–3.6 kHz — consonant clarity low
  [19, 26], // ~3.6–4.9 kHz — consonant clarity high
  [26, 36], // ~4.9–6.7 kHz — sibilance
  [36, 48], // ~6.7–9 kHz   — high-end presence
];

/** Width one bar occupies, gap included. */
const BAR_PITCH = BAR_WIDTH + BAR_GAP;

/**
 * How many bars fit in a given CSS width, at the fixed bar geometry above.
 *
 * A fluid visualizer fits more bars rather than drawing wider ones, so the
 * waveform reads the same in a 200px pill and in a full-width toolbar.
 */
const barsFitting = (width: number): number =>
  Math.max(1, Math.floor((width + BAR_GAP) / BAR_PITCH));

/** CSS width a run of `count` bars actually paints. */
const widthOfBars = (count: number): number => count * BAR_PITCH - BAR_GAP;

/** Width of the inline visualizer next to the microphone button. */
const CANVAS_WIDTH = widthOfBars(BAR_COUNT);

/**
 * Builds the per-bar frequency bins for a given bar count. The default count of
 * {@link BAR_COUNT} returns the hand-tuned {@link BAND_RANGES}; any other count
 * is spread logarithmically across the same speech-focused bin window so the
 * visualizer stays voice-reactive at any width.
 */
const getBandRanges = (count: number): [number, number][] => {
  if (count === BAR_COUNT) {
    return BAND_RANGES;
  }
  const MIN_BIN = 1;
  const MAX_BIN = 48;
  const ranges: [number, number][] = [];
  for (let i = 0; i < count; i += 1) {
    const lo = Math.round(MIN_BIN * (MAX_BIN / MIN_BIN) ** (i / count));
    const hi = Math.round(MIN_BIN * (MAX_BIN / MIN_BIN) ** ((i + 1) / count));
    ranges.push([lo, Math.max(hi, lo + 1)]);
  }
  return ranges;
};

/**
 * Canvas-based audio visualizer that draws directly in a rAF loop.
 * Bypasses React re-renders for smooth 60fps animation.
 *
 * Reads the recording stream it is handed and never opens the microphone
 * itself: a second `getUserMedia` on the same device competes with the capture
 * pipeline and breaks it outright on iOS.
 */
export function AudioVisualizer({
  stream,
  barColor,
  barCount = BAR_COUNT,
  fluid = false,
}: {
  /** Live capture stream. Nothing is drawn while this is null. */
  stream: MediaStream | null;
  barColor: string;
  /**
   * Number of frequency bars to draw. Defaults to the tuned 10-band layout.
   * Ignored when `fluid`, which fits as many bars as its container allows.
   */
  barCount?: number;
  /** Fill the container width instead of a fixed width. */
  fluid?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  // A fluid canvas is sized by its container, so the drawing surface has to
  // follow that width. Painting a fixed-width surface and letting CSS stretch it
  // to 100% is what left the bars blurry and elastic.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!fluid || !canvas) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setMeasuredWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [fluid]);

  const drawnCount = fluid ? barsFitting(measuredWidth) : barCount;
  const bands = useMemo(() => getBandRanges(drawnCount), [drawnCount]);
  const canvasWidth = fluid ? measuredWidth : widthOfBars(barCount);
  // Whatever the bar run cannot fill (up to one gap short of a full bar) is
  // split either side, so the waveform sits centred rather than left-hugging.
  const leadingSpace = Math.max(0, (canvasWidth - widthOfBars(drawnCount)) / 2);

  // Read by the draw loop rather than closed over, so a resize re-scales the
  // canvas in place instead of tearing down the audio graph and rebuilding it.
  const layoutRef = useRef({ canvasWidth, drawnCount, bands, leadingSpace });
  layoutRef.current = { canvasWidth, drawnCount, bands, leadingSpace };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    if (!stream) {
      const ctx = canvas.getContext("2d");
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let audioContext: AudioContext | null = null;

    const setup = async () => {
      try {
        audioContext = new AudioContext();
        // iOS hands back a suspended context, which would render flat bars.
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }
        if (cancelled) {
          return;
        }

        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        analyser.smoothingTimeConstant = 0.6;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const ctx = canvas.getContext("2d")!;
        const dpr = window.devicePixelRatio || 1;
        let scaledWidth = 0;

        const tick = () => {
          if (cancelled) return;
          rafId = requestAnimationFrame(tick);

          const {
            canvasWidth: width,
            drawnCount: count,
            bands: ranges,
            leadingSpace: padding,
          } = layoutRef.current;
          if (width <= 0) return;

          // Resizing the backing store resets the transform, so the device-pixel
          // scale is reapplied with it. One bar always spans BAR_WIDTH CSS
          // pixels, whatever the container: no stretching, no blur.
          if (width !== scaledWidth) {
            scaledWidth = width;
            canvas.width = width * dpr;
            canvas.height = MAX_HEIGHT * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          }

          analyser.getByteFrequencyData(dataArray);
          ctx.clearRect(0, 0, width, MAX_HEIGHT);
          ctx.fillStyle = barColor;

          for (let i = 0; i < count; i++) {
            const [lo, hi] = ranges[i];
            // Average the bins in this frequency band
            let sum = 0;
            const bins = Math.min(hi, dataArray.length) - lo;
            for (let j = lo; j < Math.min(hi, dataArray.length); j++) {
              sum += dataArray[j];
            }
            const level = bins > 0 ? sum / bins / 255 : 0;
            const h = MIN_HEIGHT + level * (MAX_HEIGHT - MIN_HEIGHT);
            const x = padding + i * BAR_PITCH;
            const y = (MAX_HEIGHT - h) / 2;

            ctx.beginPath();
            ctx.roundRect(x, y, BAR_WIDTH, h, BAR_WIDTH / 2);
            ctx.fill();
          }
        };

        rafId = requestAnimationFrame(tick);
      } catch {
        // Visualisation is decorative — never let it break the recording.
      }
    };

    void setup();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      void audioContext?.close().catch(() => undefined);
    };
  }, [stream, barColor]);

  return (
    <canvas
      ref={canvasRef}
      width={canvasWidth}
      height={MAX_HEIGHT}
      className={cn("block", fluid ? "w-full" : "shrink-0")}
      style={{ width: fluid ? "100%" : canvasWidth, height: MAX_HEIGHT }}
    />
  );
}

export type SpeechInputProps = ComponentProps<typeof Button> &
  Pick<
    UseVoiceTranscriptionOptions,
    | "onTranscript"
    | "onPermissionDenied"
    | "onError"
    | "onConnectionLost"
    | "language"
  > & {
    /** Called when recording starts and stops. */
    onRecordingChange?: (isRecording: boolean) => void;
    /**
     * Called when live transcription becomes unavailable mid-session. Recording
     * continues; the transcript arrives only once the user stops.
     */
    onLiveTranscriptChange?: (isLive: boolean) => void;
    /** Receives the live capture stream so an external visualizer can reuse it. */
    onStreamChange?: (stream: MediaStream | null) => void;
    /** Render the inline spectrum visualizer next to the button. Defaults to true. */
    showInlineVisualizer?: boolean;
  };

export type SpeechInputHandle = {
  /** Stop recording and keep the result (transcription proceeds). */
  stop: () => void;
  /** Stop recording and discard the result (no transcription is emitted). */
  cancel: () => void;
};

/**
 * Microphone button driving a live dictation session.
 *
 * A single transport is used on every platform — PCM streamed to the API, which
 * proxies it to Voxtral. The Web Speech API is deliberately not used: it
 * produces unpunctuated text, ends itself after a few seconds of silence, is
 * blocked outright by several browsers, and its quality varies per vendor.
 */
export const SpeechInput = forwardRef<SpeechInputHandle, SpeechInputProps>(
  function SpeechInput(
    {
      className,
      onTranscript,
      onPermissionDenied,
      onError,
      onConnectionLost,
      language,
      onRecordingChange,
      onLiveTranscriptChange,
      onStreamChange,
      showInlineVisualizer = true,
      disabled,
      ...props
    },
    ref,
  ) {
    const {
      isRecording,
      isFinalizing,
      isLiveTranscript,
      isAwaitingConnection,
      stream,
      start,
      stop,
      cancel,
    } = useVoiceTranscription({
      onTranscript,
      onPermissionDenied,
      onError,
      onConnectionLost,
      language,
    });

    const onRecordingChangeRef = useRef(onRecordingChange);
    onRecordingChangeRef.current = onRecordingChange;
    const onStreamChangeRef = useRef(onStreamChange);
    onStreamChangeRef.current = onStreamChange;
    const onLiveTranscriptChangeRef = useRef(onLiveTranscriptChange);
    onLiveTranscriptChangeRef.current = onLiveTranscriptChange;

    useEffect(() => {
      onRecordingChangeRef.current?.(isRecording);
    }, [isRecording]);

    useEffect(() => {
      onStreamChangeRef.current?.(stream);
    }, [stream]);

    useEffect(() => {
      onLiveTranscriptChangeRef.current?.(isLiveTranscript);
    }, [isLiveTranscript]);

    useImperativeHandle(ref, () => ({ stop, cancel }), [stop, cancel]);

    // Re-read the theme colour when a session starts so a theme change between
    // sessions is picked up on the next recording.
    const destructiveColor = useMemo(() => {
      if (typeof document === "undefined") return "#ef4444";
      const style = getComputedStyle(document.documentElement);
      return style.getPropertyValue("--color-destructive").trim() || "#ef4444";
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording]);

    return (
      <div className="inline-flex items-center">
        {/* Audio spectrum visualizer — width animates so the surrounding layout
            (e.g. a sibling flex-1 button) resizes smoothly when listening toggles. */}
        {showInlineVisualizer && (
          <div
            aria-hidden={!isRecording}
            style={{
              width: isRecording ? CANVAS_WIDTH : 0,
              marginRight: isRecording ? 6 : 0,
            }}
            className="overflow-hidden shrink-0 transition-[width,margin-right] duration-300 ease-out"
          >
            <AudioVisualizer stream={stream} barColor={destructiveColor} />
          </div>
        )}

        <Button
          type="button"
          className={cn(
            "shrink-0",
            isRecording && "text-destructive hover:text-destructive",
            className,
          )}
          disabled={disabled || isFinalizing}
          onClick={() => (isRecording ? stop() : void start())}
          {...props}
        >
          {isAwaitingConnection ? (
            // A spinner here would read as a hang: the upload is not stalled,
            // it is holding the recording until there is a network to send it on.
            <WifiOffIcon className="size-4 animate-pulse" />
          ) : isFinalizing ? (
            <Spinner className="size-4" />
          ) : isRecording ? (
            <SquareIcon className="size-3.5 fill-current" />
          ) : (
            <MicIcon className="size-4" />
          )}
        </Button>
      </div>
    );
  },
);
